/**
 * Module analytics for the project Overview: Engineering (DCI), Procurement
 * (MCI) and Fabrication & Construction. Built from the module summary payloads
 * the dashboard already fetches — no extra round trips.
 */
import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Bar, BarChart, CartesianGrid, Cell, PolarAngleAxis, RadialBar, RadialBarChart,
  ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { FileText, ShoppingCart, HardHat, Layers } from 'lucide-react';
import { usePanelTheme } from './panelTheme';
import ModuleDrilldownDrawer from './ModuleDrilldownDrawer';

const Shell = ({ title, subtitle, icon: Icon, accent, children, testId, T, projectId, module }) => {
  const [drill, setDrill] = useState(false);
  return (
    <div className="rounded-2xl overflow-hidden" data-testid={testId}
         style={{ background: T.card, border: `1px solid ${T.border}`,
                  boxShadow: T.isDark ? 'none' : '0 1px 2px rgba(15,23,42,0.04)' }}>
      <div className="px-4 pt-3.5 pb-3" style={{ background: T.sheen, borderBottom: `1px solid ${T.border}` }}>
        <div className="flex items-center gap-2">
          <span className="w-7 h-7 rounded-lg grid place-items-center shrink-0" style={{ background: `${accent}1F` }}>
            <Icon className="w-3.5 h-3.5" style={{ color: accent }} />
          </span>
          <div className="min-w-0">
            <h3 className="text-sm font-semibold leading-tight truncate" style={{ color: T.text }}>{title}</h3>
            <p className="text-[10px] truncate" style={{ color: T.muted }}>{subtitle}</p>
          </div>
          {module && (
            <button type="button" onClick={() => setDrill(true)}
              className="ml-auto shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-semibold transition-transform hover:-translate-y-0.5"
              style={{ background: `${accent}18`, color: accent, border: `1px solid ${accent}44` }}
              data-testid={`${testId}-drilldown-btn`}>
              <Layers className="w-3 h-3" /> L2 · L3
            </button>
          )}
        </div>
      </div>
      <div className="p-4 space-y-3">{children}</div>
      {drill && (
        <ModuleDrilldownDrawer projectId={projectId} module={module} onClose={() => setDrill(false)} />
      )}
    </div>
  );
};

const Stat = ({ label, value, tone, T, delay = 0 }) => (
  <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay }}
    className="rounded-xl px-2.5 py-2 transition-transform duration-200 hover:-translate-y-0.5"
    style={{ background: T.inner, border: `1px solid ${T.border}` }}>
    <div className="text-[9px] uppercase tracking-wider truncate" style={{ color: T.muted }}>{label}</div>
    <div className="text-sm font-mono font-semibold" style={{ color: tone || T.text }}>{value}</div>
  </motion.div>
);

const Meter = ({ label, pct, tone, T, right }) => (
  <div>
    <div className="flex justify-between text-[10px] mb-1">
      <span className="truncate pr-2" style={{ color: T.text2 }}>{label}</span>
      <span className="font-mono" style={{ color: T.muted }}>{right ?? `${(pct || 0).toFixed(1)}%`}</span>
    </div>
    <div className="h-1.5 rounded-full overflow-hidden" style={{ background: T.inner }}>
      <motion.div initial={{ width: 0 }} animate={{ width: `${Math.min(100, Math.max(0, pct || 0))}%` }}
        transition={{ duration: 0.8, ease: 'easeOut' }} className="h-full rounded-full"
        style={{ background: tone }} />
    </div>
  </div>
);

const pctTone = (T, v, good = 90, ok = 70) => (v >= good ? T.good : v >= ok ? T.warn : T.bad);

export const EngineeringAnalytics = ({ data, projectId }) => {
  const T = usePanelTheme();
  const k = data?.kpis || {};
  const disciplines = (data?.by_discipline || []).slice(0, 8);
  const total = k.total_deliverables || 0;
  const chart = disciplines.map((d) => ({
    name: d.discipline, Submitted: d.submitted || 0, Approved: d.approved || 0, IFC: d.ifc || 0,
  }));

  return (
    <Shell T={T} testId="engineering-analytics" icon={FileText} accent={T.violet} projectId={projectId} module="engineering"
      title="Engineering Analytics · DCI"
      subtitle={`${total} deliverables · ${(k.progress_pct || 0).toFixed(1)}% weighted progress`}>
      <div className="grid grid-cols-4 gap-2">
        <Stat T={T} label="Submitted" value={`${k.submitted || 0}`} tone={T.accent} delay={0} />
        <Stat T={T} label="Approved" value={`${k.approved || 0}`} tone={T.good} delay={0.05} />
        <Stat T={T} label="IFC issued" value={`${k.ifc_issued || 0}`} tone={T.teal} delay={0.1} />
        <Stat T={T} label="In review" value={`${k.pending_review || 0}`} tone={T.warn} delay={0.15} />
      </div>

      <div className="space-y-2">
        <Meter T={T} label="Submission coverage" tone={T.accent}
               pct={total ? (k.submitted / total) * 100 : 0}
               right={`${k.submitted || 0}/${total}`} />
        <Meter T={T} label="Approval conversion" tone={pctTone(T, k.submitted ? (k.approved / k.submitted) * 100 : 0, 70, 45)}
               pct={k.submitted ? (k.approved / k.submitted) * 100 : 0}
               right={`${k.approved || 0}/${k.submitted || 0}`} />
        <Meter T={T} label="Released for construction (IFC)" tone={T.teal}
               pct={total ? (k.ifc_issued / total) * 100 : 0}
               right={`${k.ifc_issued || 0}/${total}`} />
      </div>

      {chart.length > 0 && (
        <div style={{ height: 150 }} data-testid="engineering-discipline-chart">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chart} margin={{ top: 4, right: 4, left: -22, bottom: 0 }} barGap={1}>
              <CartesianGrid strokeDasharray="3 3" stroke={T.grid} vertical={false} />
              <XAxis dataKey="name" tick={{ fill: T.muted, fontSize: 9 }} interval={0} angle={-20} textAnchor="end" height={34} />
              <YAxis tick={{ fill: T.muted, fontSize: 9 }} />
              <Tooltip contentStyle={{ background: T.card, border: `1px solid ${T.border}`, fontSize: 11, color: T.text }} />
              <Bar dataKey="Submitted" fill={T.accent} radius={[2, 2, 0, 0]} />
              <Bar dataKey="Approved"  fill={T.good}   radius={[2, 2, 0, 0]} />
              <Bar dataKey="IFC"       fill={T.teal}   radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </Shell>
  );
};

export const ProcurementAnalytics = ({ data, projectId }) => {
  const T = usePanelTheme();
  const k = data?.kpis || {};
  const total = k.total_orders || 0;
  const value = k.total_value || 0;
  const pipeline = [
    { name: 'Ordered',   value: k.ordered || 0,       fill: T.accent },
    { name: 'Mfg',       value: k.manufacturing || 0, fill: T.violet },
    { name: 'In transit',value: k.in_transit || 0,    fill: T.teal },
    { name: 'Delivered', value: k.delivered || 0,     fill: T.good },
  ];
  const money = value >= 1e7 ? `₹${(value / 1e7).toFixed(2)} Cr` : `₹${(value / 1e5).toFixed(1)} L`;

  return (
    <Shell T={T} testId="procurement-analytics" icon={ShoppingCart} accent={T.accent} projectId={projectId} module="procurement"
      title="Procurement Analytics · MCI"
      subtitle={`${total} orders · ${money} committed · ${(k.progress_pct || 0).toFixed(1)}% progress`}>
      <div className="grid grid-cols-4 gap-2">
        <Stat T={T} label="Delivered" value={`${k.delivered || 0}`} tone={T.good} delay={0} />
        <Stat T={T} label="At risk" value={`${k.at_risk_count || 0}`} tone={T.warn} delay={0.05} />
        <Stat T={T} label="Delayed" value={`${k.delayed_count || 0}`} tone={(k.delayed_count || 0) > 0 ? T.bad : T.muted} delay={0.1} />
        <Stat T={T} label="Lead time" value={`${Math.round(k.avg_lead_time || 0)}d`} tone={T.text} delay={0.15} />
      </div>

      <div className="flex items-center gap-3">
        <div style={{ width: 104, height: 104 }} data-testid="procurement-otd-gauge">
          <ResponsiveContainer width="100%" height="100%">
            <RadialBarChart innerRadius="68%" outerRadius="100%" data={[{ name: 'OTD', value: k.on_time_delivery_rate || 0 }]}
              startAngle={90} endAngle={-270}>
              <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
              <RadialBar dataKey="value" cornerRadius={8} background={{ fill: T.inner }}
                         fill={pctTone(T, k.on_time_delivery_rate || 0)} />
            </RadialBarChart>
          </ResponsiveContainer>
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-wider" style={{ color: T.muted }}>On-time delivery</div>
          <div className="text-2xl font-bold font-mono leading-tight"
               style={{ color: pctTone(T, k.on_time_delivery_rate || 0) }}>
            {(k.on_time_delivery_rate || 0).toFixed(1)}%
          </div>
          <div className="space-y-1.5 mt-2">
            {pipeline.map((p) => (
              <Meter key={p.name} T={T} label={p.name} tone={p.fill}
                     pct={total ? (p.value / total) * 100 : 0} right={`${p.value}`} />
            ))}
          </div>
        </div>
      </div>
    </Shell>
  );
};

export const ConstructionAnalytics = ({ data, projectId }) => {
  const T = usePanelTheme();
  const k = data?.kpis || {};
  const pi = k.productivity_index ?? 1;
  const bars = [
    { name: 'Planned', value: k.planned_pct || 0, fill: T.accent },
    { name: 'Earned',  value: k.earned_pct || 0,  fill: T.teal },
    { name: 'Actual',  value: k.actual_pct || 0,  fill: T.good },
  ];

  return (
    <Shell T={T} testId="construction-analytics" icon={HardHat} accent={T.warn} projectId={projectId} module="construction"
      title="Fabrication & Construction Analytics"
      subtitle={`${(k.actual_pct || 0).toFixed(1)}% actual vs ${(k.planned_pct || 0).toFixed(1)}% planned · PI ${pi.toFixed(2)}`}>
      <div className="grid grid-cols-4 gap-2">
        <Stat T={T} label="Productivity" value={pi.toFixed(2)} tone={pi >= 1 ? T.good : pi >= 0.9 ? T.warn : T.bad} delay={0} />
        <Stat T={T} label="Workfronts" value={`${k.workfront_availability ?? 0}%`} tone={pctTone(T, k.workfront_availability ?? 0, 85, 60)} delay={0.05} />
        <Stat T={T} label="Rework" value={`${k.rework_rate ?? 0}%`} tone={(k.rework_rate ?? 0) > 2 ? T.bad : T.good} delay={0.1} />
        <Stat T={T} label="Installed" value={`${k.installed_qty ?? 0}/${k.planned_qty ?? 0}`} tone={T.text} delay={0.15} />
      </div>

      <div style={{ height: 118 }} data-testid="construction-progress-chart">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={bars} layout="vertical" margin={{ top: 2, right: 28, left: 6, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={T.grid} horizontal={false} />
            <XAxis type="number" domain={[0, 100]} tick={{ fill: T.muted, fontSize: 9 }} tickFormatter={(v) => `${v}%`} />
            <YAxis type="category" dataKey="name" width={54} tick={{ fill: T.muted, fontSize: 10 }} />
            <Tooltip contentStyle={{ background: T.card, border: `1px solid ${T.border}`, fontSize: 11, color: T.text }}
                     formatter={(v) => [`${Number(v).toFixed(1)}%`, 'Progress']} />
            <Bar dataKey="value" radius={[0, 4, 4, 0]} barSize={16}>
              {bars.map((b) => <Cell key={b.name} fill={b.fill} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <Stat T={T} label="Man-hours earned" value={(k.man_hours_earned || 0).toLocaleString('en-IN')} tone={T.teal} />
        <Stat T={T} label="Man-hours spent" value={(k.man_hours_actual || 0).toLocaleString('en-IN')}
              tone={(k.man_hours_actual || 0) > (k.man_hours_earned || 0) ? T.warn : T.good} />
      </div>
    </Shell>
  );
};
